import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import RatingStars from './RatingStars';
import ImageUpload from './ImageUpload';
import reviewService from '../../services/reviewService';
import './ReviewForm.css';

const MAX_COMMENT_LENGTH = 2000;
const MAX_TITLE_LENGTH = 200;

const ReviewForm = ({
  productId,
  existingReview = null,
  onSuccess,
  onCancel,
  isLoggedIn = true
}) => {
  // Form state
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [title, setTitle] = useState(existingReview?.title || '');
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [recommend, setRecommend] = useState(existingReview?.recommend ?? true);
  const [images, setImages] = useState(existingReview?.images || []);
  const [agreedToGuidelines, setAgreedToGuidelines] = useState(!!existingReview);

  // UI state
  const [canReview, setCanReview] = useState(null);
  const [eligibilityReason, setEligibilityReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);

  // Check eligibility on mount (only for new reviews)
  useEffect(() => {
    // Not logged in - show sign in prompt
    if (!isLoggedIn) {
      setCanReview(false);
      setEligibilityReason('not_logged_in');
      setLoading(false);
      return;
    }

    // Editing existing review - allow
    if (existingReview) {
      setCanReview(true);
      setLoading(false);
      return;
    }

    // Check with backend if user can review
    const checkEligibility = async () => {
      try {
        const response = await reviewService.canReview(productId);
        setCanReview(response.data.canReview);
        if (!response.data.canReview) {
          setEligibilityReason(response.data.reason || 'You cannot review this product.');
        }
      } catch (error) {
        console.error('Error checking eligibility:', error);

        // Check if it's an authentication error (401/403)
        const status = error?.status || error?.response?.status;
        if (status === 401 || status === 403) {
          // If we thought user was logged in but got 401/403, session may have expired
          setCanReview(false);
          setEligibilityReason('session_expired');
        } else {
          // Generic error - show retry option
          setCanReview(false);
          setEligibilityReason('eligibility_check_failed');
        }
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [productId, existingReview, isLoggedIn]);

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (rating === 0) {
      newErrors.rating = 'Please select a rating';
    }

    if (!title || title.trim().length === 0) {
      newErrors.title = 'Please enter a review title';
    } else if (title.length > MAX_TITLE_LENGTH) {
      newErrors.title = `Title must be ${MAX_TITLE_LENGTH} characters or less`;
    }

    if (comment.length > MAX_COMMENT_LENGTH) {
      newErrors.comment = `Comment must be ${MAX_COMMENT_LENGTH} characters or less`;
    }

    if (typeof recommend !== 'boolean') {
      newErrors.recommend = 'Please indicate if you would recommend this product';
    }

    if (!agreedToGuidelines) {
      newErrors.guidelines = 'You must agree to the review guidelines';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      const reviewData = {
        rating,
        title: title.trim(),
        comment: comment.trim(),
        recommend,
        images: images.map(img => ({
          url: img.url,
          thumbnailUrl: img.thumbnailUrl
        }))
      };

      let result;
      if (existingReview) {
        // Update existing review
        result = await reviewService.updateReview(existingReview.id, reviewData);
      } else {
        // Create new review
        result = await reviewService.createReview(productId, reviewData);
      }

      // Show success message
      setShowSuccess(true);

      // Call success callback after a delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(result.data);
        }
      }, 2000);
    } catch (error) {
      console.error('Error submitting review:', error);

      const errorMessage = error.response?.data?.message ||
        'Failed to submit review. Please try again.';

      setErrors({
        submit: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Character counter helper
  const getCommentCounterClass = () => {
    if (comment.length > MAX_COMMENT_LENGTH) {
      return 'counter-error';
    }
    return comment.length > 0 ? 'counter-success' : '';
  };

  const isSubmitEnabled = () => {
    return (
      rating > 0 &&
      title.trim().length > 0 &&
      title.length <= MAX_TITLE_LENGTH &&
      comment.length <= MAX_COMMENT_LENGTH &&
      typeof recommend === 'boolean' &&
      agreedToGuidelines &&
      !submitting
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="review-form-container">
        <div className="review-form-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Build return URL for login redirect (includes hash for reviews tab)
  const getLoginRedirectUrl = () => {
    const currentPath = window.location.pathname;
    const hash = '#contact'; // Reviews tab hash
    return `/login?redirect=${encodeURIComponent(currentPath + hash)}`;
  };

  const getRegisterRedirectUrl = () => {
    const currentPath = window.location.pathname;
    const hash = '#contact';
    return `/register?redirect=${encodeURIComponent(currentPath + hash)}`;
  };

  // Not eligible state - show appropriate message based on reason
  if (!canReview) {
    return (
      <div className="review-form-container">
        <h4>Write a Review</h4>
        <div className="review-form-not-eligible">
          {/* Not logged in */}
          {eligibilityReason === 'not_logged_in' && (
            <>
              <div className="not-eligible-icon">🔐</div>
              <h5>Sign In to Write a Review</h5>
              <p>You need to be signed in to write a review.</p>
              <p className="text-muted">Reviews are limited to verified purchasers.</p>
              <div className="not-eligible-actions">
                <Link to={getLoginRedirectUrl()} className="btn btn-primary">
                  Sign In
                </Link>
                <Link to={getRegisterRedirectUrl()} className="btn btn-secondary">
                  Create Account
                </Link>
              </div>
            </>
          )}

          {/* Session expired (was logged in but API returned 401/403) */}
          {eligibilityReason === 'session_expired' && (
            <>
              <div className="not-eligible-icon">🔄</div>
              <h5>Session Expired</h5>
              <p>Your session has expired. Please sign in again to write a review.</p>
              <div className="not-eligible-actions">
                <Link to={getLoginRedirectUrl()} className="btn btn-primary">
                  Sign In Again
                </Link>
              </div>
            </>
          )}

          {/* Eligibility check failed - most likely user hasn't purchased */}
          {eligibilityReason === 'eligibility_check_failed' && (
            <>
              <div className="not-eligible-icon">🛒</div>
              <h5>Purchase Required</h5>
              <p>Only customers who have purchased this product can write a review.</p>
              <p className="text-muted">This helps ensure reviews come from verified buyers.</p>
            </>
          )}

          {/* Backend returned a specific reason (e.g., must purchase first) */}
          {eligibilityReason !== 'not_logged_in' &&
           eligibilityReason !== 'session_expired' &&
           eligibilityReason !== 'eligibility_check_failed' && (
            <>
              <div className="not-eligible-icon">🛒</div>
              <h5>Purchase Required</h5>
              <p>{eligibilityReason}</p>
              <p className="text-muted">Only verified purchasers can review this product.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Success state
  if (showSuccess) {
    return (
      <div className="review-form-container">
        <div className="review-form-success">
          <div className="success-icon">✓</div>
          <h4>Review Submitted Successfully!</h4>
          <p>
            Your review has been submitted and is pending approval.
            It will appear on the product page once approved by our team.
          </p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="review-form-container">
      <h4>{existingReview ? 'Edit Your Review' : 'Write a Review'}</h4>

      <form onSubmit={handleSubmit} className="review-form">
        {/* Rating */}
        <div className="form-group">
          <label className="required">Your Rating</label>
          <RatingStars
            rating={rating}
            size="lg"
            interactive={true}
            onChange={setRating}
            disabled={submitting}
          />
          {errors.rating && (
            <div className="field-error">{errors.rating}</div>
          )}
        </div>

        {/* Title */}
        <div className="form-group">
          <label className="required">Review Title</label>
          <input
            type="text"
            className="form-control"
            placeholder="Summarize your experience"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={MAX_TITLE_LENGTH}
            disabled={submitting}
          />
          <div className="field-hint">
            {title.length}/{MAX_TITLE_LENGTH} characters
          </div>
          {errors.title && (
            <div className="field-error">{errors.title}</div>
          )}
        </div>

        {/* Comment */}
        <div className="form-group">
          <label>Your Review (optional)</label>
          <textarea
            className="form-control"
            placeholder="Tell us more about your experience with this product (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={6}
            maxLength={MAX_COMMENT_LENGTH}
            disabled={submitting}
          />
          <div className={`field-hint ${getCommentCounterClass()}`}>
            {comment.length}/{MAX_COMMENT_LENGTH} characters
          </div>
          {errors.comment && (
            <div className="field-error">{errors.comment}</div>
          )}
        </div>

        {/* Image Upload */}
        <div className="form-group">
          <ImageUpload
            images={images}
            onChange={setImages}
            disabled={submitting}
          />
        </div>

        {/* Recommend Field */}
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={recommend}
              onChange={(e) => setRecommend(e.target.checked)}
              disabled={submitting}
            />
            <span>I would recommend this product to a friend</span>
          </label>
          {errors.recommend && (
            <div className="field-error">{errors.recommend}</div>
          )}
        </div>

        {/* Guidelines Agreement */}
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={agreedToGuidelines}
              onChange={(e) => setAgreedToGuidelines(e.target.checked)}
              disabled={submitting}
            />
            <span>
              I have read and agree to the{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => setShowGuidelines(true)}
              >
                review guidelines
              </button>
            </span>
          </label>
          {errors.guidelines && (
            <div className="field-error">{errors.guidelines}</div>
          )}
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="submit-error" role="alert">
            {errors.submit}
          </div>
        )}

        {/* Actions */}
        <div className="form-actions">
          {onCancel && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!isSubmitEnabled()}
          >
            {submitting ? (
              <>
                <span className="spinner-small"></span>
                Submitting...
              </>
            ) : (
              existingReview ? 'Update Review' : 'Submit Review'
            )}
          </button>
        </div>
      </form>

      {/* Guidelines Modal */}
      {showGuidelines && (
        <div className="modal-overlay" onClick={() => setShowGuidelines(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Review Guidelines</h5>
              <button
                type="button"
                className="close-button"
                onClick={() => setShowGuidelines(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <h6>Please follow these guidelines:</h6>
              <ul>
                <li>Be honest and provide constructive feedback</li>
                <li>Focus on the product, not the seller or shipping</li>
                <li>Don't include personal information</li>
                <li>Keep language appropriate and respectful</li>
                <li>Don't include promotional content or spam</li>
                <li>Only review products you've actually used</li>
              </ul>
              <p className="text-muted">
                Reviews that violate these guidelines may be removed.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowGuidelines(false)}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewForm;
